import React, { useEffect, useState } from 'react'
import { signOut } from 'firebase/auth'
import { useNavigate, useLocation } from 'react-router-dom'
import LogoutIcon from '@mui/icons-material/Logout'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import { collection, getDocs, query, where } from 'firebase/firestore'
import {
  Box,
  Button,
  Container,
  Typography,
  CircularProgress,
  ImageList,
  ImageListItem,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material'
import { useSnackbar } from '../contexts/SnackbarContext'
import { auth, db } from '../firebase'
import { useAuthWatcher } from '../hooks/useAuthWatcher'
import Pagination from '../components/Pagination'
import { Shop } from '../type/common'
import noImage from '../assets/no-img.jpg'
import { TagAutocomplete } from '../components/TagAutocomplete'
import logo from '../assets/logo.svg'
import { EmptyShopsState } from '../components/EmptyShopsState'
import { InviteDialog } from '../components/InviteDialog'

type LocationState = {
  returnPage?: number
  selectedTags?: string[]
}

export const HomePage = () => {
  const { isLoading, user } = useAuthWatcher()
  const userId = user?.uid
  const [isLoadingShops, setIsLoadingShops] = useState(true)
  type ShopData = Shop & { id: string | undefined }
  const [shops, setShops] = useState<ShopData[]>([])
  const [availableTags, setAvailableTags] = useState<string[]>([])

  const location = useLocation()
  const navigate = useNavigate()

  // stateからページ番号とタグを取得
  const locationState = location.state as LocationState
  const initialPage = locationState?.returnPage || 1
  const initialTags = locationState?.selectedTags || []

  const { showSnackbar } = useSnackbar()
  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags)
  const [currentPage, setCurrentPage] = useState(initialPage)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set())
  const itemsPerPage = 10

  // locationStateが変更されたら、stateをクリア
  useEffect(() => {
    if (locationState?.returnPage || locationState?.selectedTags) {
      // stateを使用した後はクリアする
      window.history.replaceState({}, document.title)
    }
  }, [locationState])

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const filteredItems = shops.filter((shop) => {
    if (selectedTags.length === 0) return true

    const tagsArray = Array.isArray(shop.tags)
      ? shop.tags
      : typeof shop.tags === 'string'
        ? shop.tags
            .split(/[、,]/)
            .map((tag) => tag.trim())
            .filter(Boolean)
        : []

    return selectedTags.some((tag) => tagsArray.includes(tag))
  })
  const currentFilteredItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem)

  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.up('sm'))
  const isMd = useMediaQuery(theme.breakpoints.up('md'))

  // デバイスごとにcolsの値を変更
  const getCols = () => {
    if (isMd) return 4
    if (isSm) return 3
    return 2
  }

  // 店舗詳細ページへの遷移（現在のページ番号を渡す）
  const handleShopClick = (shopId: string | undefined) => {
    if (shopId) {
      navigate(`/shop/${shopId}`, {
        state: {
          fromPage: currentPage,
          selectedTags,
        },
      })
    }
  }

  const fetchShops = async () => {
    setIsLoadingShops(true)
    // returnPageがある場合はタグをクリアしない（既に初期値として設定済み）
    if (!locationState?.returnPage && !locationState?.selectedTags) {
      setSelectedTags([])
    }
    try {
      const q = query(collection(db, 'shops'), where('userId', '==', userId))
      const querySnapshot = await getDocs(q)
      let shopData: ShopData[] = querySnapshot.docs.map((doc) => {
        const data = doc.data() as Shop
        return {
          id: doc.id,
          name: data.name,
          description: data.description,
          address: data.address,
          tags: data.tags,
          imageUrl: data.imageUrl,
          createdAt: data.createdAt,
          userId: data.userId,
        }
      })

      shopData = shopData.sort((a, b) => {
        const dateA = a.createdAt ? a.createdAt.toDate().getTime() : 0
        const dateB = b.createdAt ? b.createdAt.toDate().getTime() : 0
        return dateB - dateA
      })

      setShops(shopData)
    } catch (error) {
      showSnackbar('予期せぬエラーです🙇', 'error')
    } finally {
      setIsLoadingShops(false)
    }
  }

  const handleTagChange = (newValue: string[]) => {
    setSelectedTags(newValue)
    setCurrentPage(1)
  }

  const fetchTags = async () => {
    const q = query(collection(db, 'shops'), where('userId', '==', userId))
    const querySnapshot = await getDocs(q)
    const allTags = querySnapshot.docs.flatMap((doc) => doc.data().tags || [])
    const uniqueTags = Array.from(new Set(allTags))
    setAvailableTags(uniqueTags)
  }

  useEffect(() => {
    if (!isLoading) {
      fetchShops()
      fetchTags()
    }
  }, [isLoading])

  useEffect(() => {
    // returnPageがある場合はページをリセットしない
    if (!locationState?.returnPage) {
      setCurrentPage(1)
    }
  }, [selectedTags])

  const handleCopyUrl = async () => {
    await navigator.clipboard.writeText(`${window.location.origin}/signUp`)
    showSnackbar('コピーしました', 'success')
  }

  const handleSignOut = () => {
    signOut(auth)
      .then(() => {
        navigate('/login')
      })
      .catch(() => {
        showSnackbar('予期せぬエラーです🙇', 'error')
      })
  }

  return (
    <>
      <Container
        maxWidth="md"
        sx={{
          marginBottom: '12px',
          paddingBottom: '60px',
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" my={2}>
          <Box
            component="img"
            src={logo}
            alt="Chopsticks"
            sx={{
              height: 25,
              width: 'auto',
            }}
          />
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => setInviteDialogOpen(true)} color="primary">
              <PersonAddOutlinedIcon />
            </IconButton>
            <Button
              onClick={handleSignOut}
              color="inherit"
              variant="text"
              endIcon={<LogoutIcon />}
              size="small"
            >
              ログアウト
            </Button>
          </Box>
        </Box>

        {isLoadingShops ? (
          <Box height="50vh" display="flex" alignItems="center" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : shops.length === 0 ? (
          // データが0件の場合は空の状態を表示
          <EmptyShopsState />
        ) : (
          // データがある場合のみ検索とリストを表示
          <>
            <TagAutocomplete
              value={selectedTags}
              options={availableTags}
              onChange={handleTagChange}
            />

            <ImageList cols={getCols()} gap={1}>
              {currentFilteredItems.map((shop) => (
                <ImageListItem
                  key={shop.id}
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease-in-out',
                    position: 'relative',
                    aspectRatio: '4 / 5',
                    overflow: 'hidden',
                  }}
                  onClick={() => handleShopClick(shop.id)}
                >
                  {!loadedImages.has(shop.id ?? '') && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.100',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  )}
                  <img
                    src={shop.imageUrl || noImage}
                    alt={shop.name}
                    loading="lazy"
                    onLoad={() => setLoadedImages((prev) => new Set(prev).add(shop.id ?? ''))}
                    onError={() => setLoadedImages((prev) => new Set(prev).add(shop.id ?? ''))}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />

                  {/* グラデーションオーバーレイ */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '35%',
                      background:
                        'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0.4) 100%)',
                      pointerEvents: 'none',
                    }}
                  />

                  {/* 店舗名とタグ */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      p: 1.5,
                      color: 'white',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '14px',
                        fontWeight: 'bold',
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        lineHeight: 1.3,
                        mb: 0.5,
                      }}
                    >
                      {shop.name}
                    </Typography>
                  </Box>
                </ImageListItem>
              ))}
            </ImageList>

            <Pagination
              itemsPerPage={itemsPerPage}
              totalItems={filteredItems.length}
              currentPage={currentPage}
              paginate={paginate}
            />
          </>
        )}
      </Container>

      {/* データがある場合のみ固定ボタンを表示 */}
      {!isLoadingShops && shops.length > 0 && (
        <Box position="fixed" bottom="20px" right="16px">
          <Button
            color="primary"
            variant="contained"
            startIcon={<AddCircleIcon />}
            onClick={() => navigate('/create-shop')}
            sx={{
              fontWeight: 'bold',
            }}
          >
            お店を追加する
          </Button>
        </Box>
      )}

      <InviteDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onCopy={handleCopyUrl}
      />
    </>
  )
}
